import React, { FC } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { StakeholderType, stakeholderTypeTitle } from '../../../types/stakeholder.types';
import { activeColors, fillColors, strokeColors } from './cap-table-graph';

type EventGraphProps = {
  isDesktop: boolean;
  isLoadingFinished: boolean;
  isTablet: boolean;
};

export const EmptyCapTableGraph: FC<EventGraphProps> = ({
  isDesktop,
  isTablet,
  isLoadingFinished,
}) => {
  const isMobile = !isDesktop && !isTablet;
  const data = [
    { name: 'A', value: 600 },
    { name: 'B', value: 400 },
    { name: 'C', value: 400 },
    { name: 'D', value: 400 },
    { name: 'E', value: 400 },
    { name: 'F', value: 4000 },
    { name: 'G', value: 400 },
    { name: 'GG', value: 400 },
    { name: 'FF', value: 400 },
  ];

  const activeIndex = -1;
  return (
    <div
      className={twMerge(
        'centered relative flex h-full min-h-0 flex-1 items-center rounded-md bg-white shadow-sm',
        isDesktop ? 'px-6 py-[30px]' : isTablet ? 'px-4 py-5' : 'p-3 pt-8',
      )}
    >
      {isLoadingFinished && (
        <span className="absolute z-1 text-label-md font-medium text-gray-500">
          <AppFormattedMessage id="OWNERSHIP_SNAPSHOT_WILL_BE_DISPLAYED_HERE" />
        </span>
      )}

      <div
        className={twMerge('flex w-full items-center justify-center gap-8', isMobile && 'flex-col')}
      >
        <div
          className={twMerge(
            isDesktop ? 'size-[294px]' : isTablet ? 'size-[188px]' : 'size-[280px]',
          )}
        >
          <ResponsiveContainer className={'opacity-50 blur-md'} height="100%" width="100%">
            <PieChart>
              <Pie
                cx="50%"
                cy="50%"
                data={data}
                dataKey="value"
                innerRadius="50%"
                isAnimationActive={false}
                outerRadius="100%"
              >
                {data.map((_, index) => (
                  <Cell
                    fill={
                      activeIndex === index
                        ? activeColors[index % activeColors.length]
                        : fillColors[index % fillColors.length]
                    }
                    fillOpacity={1}
                    key={index}
                    stroke={strokeColors[index % strokeColors.length]}
                    strokeDasharray="5 5"
                  />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div
          className={twMerge(
            'flex max-w-[232px] select-none flex-wrap gap-x-4 gap-y-2 pt-4',
            isMobile && 'max-w-full justify-center',
          )}
        >
          {data.map((_, index) => (
            <div
              className={twMerge(
                'flex w-[108px] flex-col gap-2 pb-2 blur-md',
                isMobile && 'items-center',
              )}
              key={`${index}`}
            >
              <span
                className="text-label-md font-[500] text-gray-600"
                style={{
                  color: '#344054',
                  transition: 'color 0.5s ease',
                }}
              >
                <AppFormattedMessage id={stakeholderTypeTitle[StakeholderType.EMPLOYEE]} />
              </span>
              <div className="flex items-center gap-2">
                <div
                  className="h-[5px] w-3 rounded"
                  style={{
                    backgroundColor:
                      activeIndex === index
                        ? activeColors[index % activeColors.length]
                        : fillColors[index % fillColors.length],
                    border:
                      activeIndex === index
                        ? `1px solid ${activeColors[index % activeColors.length]}`
                        : `1px solid ${fillColors[index % fillColors.length]}`,
                    transition: 'background-color 0.5s ease, border 0.5s ease',
                  }}
                />
                <span
                  className="whitespace-nowrap text-sm font-[450]"
                  style={{
                    color: '#344054',
                    transition: 'color 0.5s ease',
                  }}
                >
                  10 %
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
